import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useHasQueueMetrics } from '@/api/use-branch-features';
import { env } from '@/config/env';
import { reactQueryKeys } from '@/config/react-query';
import { useCodWeb } from '@/hooks/use-codweb';
import { fetchQueueRequest } from '@/lib/fetch-queue-request';
import { getApiHeaders } from '@/lib/getApiHeaders';
import {
  HttpError,
  apiErrorCodes,
  queueRetryDelay,
  shouldRetryQueueRequest,
} from '@/lib/http-error';

type QueueLoginParams = { codWeb: string; extension: string };

const queueLogin = async (params: QueueLoginParams) => {
  const route = `${env.VITE_BACKEND_URL}/api/${params.codWeb}/queues/extensions/${params.extension}/login`;
  const headers = await getApiHeaders();

  await fetchQueueRequest(
    route,
    { method: 'PATCH', headers },
    async (response) => {
      if (!response.ok) throw await HttpError.fromResponse(response);
    }
  );
};

const pendingRelogins = new Map<string, Promise<void>>();

// Concurrent pauses (missed call and manual) must share one login request.
export const reloginOnQueues = (params: QueueLoginParams) => {
  const key = `${params.codWeb}:${params.extension}`;
  const pending =
    pendingRelogins.get(key) ??
    queueLogin(params).finally(() => pendingRelogins.delete(key));
  pendingRelogins.set(key, pending);
  return pending;
};

export const useQueueLogin = () => {
  const codWeb = useCodWeb();
  const hasQueueMetrics = useHasQueueMetrics();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (extension: string) => {
      if (hasQueueMetrics !== true)
        throw new HttpError(409, apiErrorCodes.QUEUEMETRICS_NOT_CONFIGURED);
      return queueLogin({ codWeb, extension });
    },
    retry: shouldRetryQueueRequest,
    retryDelay: queueRetryDelay,
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: [reactQueryKeys.pauseStatus],
      }),
  });
};
